import { Free } from "@/components/forms/MonthlyCostField.tsx";
import { CurrencyMonthly } from "@/components/l10n/Currency.tsx";
import { eBillingAccountQueryAtom } from "@/modules/billing/billingAccountQuery.ts";
import { FREE_PRICE, type Price } from "@/modules/pricing/resourcePricing.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";

export interface MonthlyPriceWithDiscountProps {
    price: Price;
    billingAccountId?: number;
    showDiscount?: boolean;
    /** Show "Free" text when the (discounted) price is zero. */
    allowFree: boolean;
    renderPrice?: (params: { price: Price; discountedHourlyAmount: number; discountPercentage?: number }) => ReactNode;
}

export function PriceWithDiscount({
    price,
    billingAccountId,
    allowFree = false,
    renderPrice = ({ price, discountedHourlyAmount }) => {
        if (allowFree && !discountedHourlyAmount) {
            return <span className="color-success">{Free}</span>;
        }
        return <CurrencyMonthly value={discountedHourlyAmount} isNotFree={price !== FREE_PRICE} />;
    },
}: MonthlyPriceWithDiscountProps) {
    const data = useSuspenseQueryAtom(eBillingAccountQueryAtom);
    const billingAccount = billingAccountId ? data.get(billingAccountId) : undefined;

    const discountPercentage = billingAccount?.account.discount_percentage ?? 0;
    const discountedHourlyAmount = discountPercentage
        ? price.hourly * ((100 - discountPercentage) / 100)
        : price.hourly;

    return renderPrice({ price, discountedHourlyAmount, discountPercentage });
}

interface FullMonthlyPriceWithDiscountProps extends MonthlyPriceWithDiscountProps {
    showDiscount?: boolean;
}

export function FullPriceWithDiscount({
    price,
    billingAccountId,
    showDiscount,
    allowFree,
    renderPrice = ({ price, discountedHourlyAmount, discountPercentage }) => {
        const hourlyAmount = price.hourly;
        return discountedHourlyAmount !== hourlyAmount ? (
            <>
                <span className="color-success inline-block pr-1">
                    {allowFree && !discountedHourlyAmount ? Free : <CurrencyMonthly value={discountedHourlyAmount} />}{" "}
                    {showDiscount && (
                        <span className="font-normal lowercase">
                            ({discountPercentage}% <Trans>Discount</Trans>)
                        </span>
                    )}
                </span>
                <span className="line-through">
                    <CurrencyMonthly value={hourlyAmount} />
                </span>
            </>
        ) : (
            <CurrencyMonthly value={discountedHourlyAmount} />
        );
    },
}: FullMonthlyPriceWithDiscountProps) {
    return (
        <PriceWithDiscount
            price={price}
            billingAccountId={billingAccountId}
            allowFree={allowFree}
            renderPrice={renderPrice}
        />
    );
}
